
import React, { useCallback, useEffect, useState } from 'react';
import { useObserver } from "mobx-react";
import { storeContext } from '../../context';

import {
    Form,
    Input,
    Row,
    Col,
    Button,
    Modal,
    Table,
    Select,
    Popover,
    Icon,
} from 'antd';

import RegistryForm from '../RegistryForm';
import translate from '../../i18n/translate';
import CustomAutoComplete from '../CustomAutoComplete';
import DeploySettings from './DeploySettings';
import DeployNsSettings from './DeployNsSettings';
import DeployAsSettings from './DeployAsSettings';
import api from '../../helpers/api';
import { Bus } from '../../helpers/utils';

const columns = [
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
    },
    {
        title: translate('default.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
    },
    {
        title: translate('application.name'),
        dataIndex: 'application',
        key: 'application',
    },
    {
        title: translate('deploy.applicationService'),
        dataIndex: 'application_service_name',
        key: 'application_service_name',
    },
    {
        title: translate('deploy.networkServiceFallback'),
        dataIndex: 'network_service_name',
        key: 'network_service_name',
    },
];

const networkServiceFallbackHelpContent = (
    <div style={{ maxWidth: 300, lineHeight: 1.55, fontSize: 13, color: 'rgba(0, 0, 0, 0.75)' }}>
        <div style={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)', marginBottom: 6 }}>
            {translate('deploy.networkServiceFallbackTooltipTitle')}
        </div>
        <p style={{ margin: '0 0 10px' }}>{translate('deploy.networkServiceFallbackTooltipBody')}</p>
        <div style={{
            padding: '8px 10px',
            background: '#f0f5ff',
            borderRadius: 4,
            fontSize: 12,
            color: 'rgba(0, 0, 0, 0.65)',
            borderLeft: '3px solid #1890ff',
            lineHeight: 1.45,
        }}>
            {translate('deploy.networkServiceFallbackTooltipHint')}
        </div>
    </div>
);

const networkServiceFallbackLabel = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {translate('deploy.networkServiceFallback')}
        <Popover placement="topLeft" trigger="hover" content={networkServiceFallbackHelpContent}>
            <Icon type="info-circle" style={{ color: '#1890ff', cursor: 'help', fontSize: 14 }} />
        </Popover>
    </span>
);

const labelCol = {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 12,
    xl: 10,
};

let ContentForm = () => {
    const { Option } = Select;
    const store = React.useContext(storeContext);
    const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);
    const [savingDeviceType, setSavingDeviceType] = useState(false);
    const [loadingAvailableDeviceTypes, setLoadingAvailableDeviceTypes] = useState(false);
    const [loadingNetworkServices, setLoadingNetworkServices] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [removingDeviceTypeId, setRemovingDeviceTypeId] = useState(undefined);
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [availableDeviceTypes, setAvailableDeviceTypes] = useState([]);
    const [availableNetworkServices, setAvailableNetworkServices] = useState([]);
    const [deviceTypeForm, setDeviceTypeForm] = useState({
        device_type_id: undefined,
        network_service_id: undefined,
        network_service: null,
    });
    const canManageDeviceTypes = false;

    const handleChange = (field, event) => {
        store.currentItem[field] = event.target.value
    }

    const handleComboSelect = async (field, value) => {
        console.log(field, value);
        store.currentItem[field] = value;
    }

    const listDeviceTypes = useCallback(async () => {
        if (!store.currentItem || !store.currentItem.id) {
            setDeviceTypes([]);
            return;
        }

        setLoadingDeviceTypes(true);
        try {
            const { data } = await api.get(`/deploy_external_services_settings/deploy/${store.currentItem.id}`);
            const result = (data && data.result) ? data.result : [];
            const groupedByDeviceType = result.reduce((acc, item) => {
                if (!item.device_type_id) {
                    return acc;
                }

                const deviceTypeId = Number(item.device_type_id);
                if (!acc[deviceTypeId]) {
                    acc[deviceTypeId] = {
                        ...item,
                        applicationServiceNames: new Set(),
                        networkServiceNames: new Set(),
                    };
                }

                if (item.application_service_name) {
                    acc[deviceTypeId].applicationServiceNames.add(item.application_service_name);
                }
                if (item.network_service_name) {
                    acc[deviceTypeId].networkServiceNames.add(item.network_service_name);
                }
                return acc;
            }, {});

            const uniqueDeviceTypes = Object.values(groupedByDeviceType).map((item) => ({
                ...item,
                application_service_name: Array.from(item.applicationServiceNames).join(', '),
                network_service_name: Array.from(item.networkServiceNames).join(', '),
            }));

            setDeviceTypes(uniqueDeviceTypes);
        } catch (error) {
            setDeviceTypes([]);
            store.error = { message: translate('default.errorOccurred') };
        } finally {
            setLoadingDeviceTypes(false);
        }
    }, [store]);

    useEffect(() => {
        listDeviceTypes();
    }, [listDeviceTypes]);

    useEffect(() => {
        const handleReloadCustomTabs = () => {
            listDeviceTypes();
        };
        const handleReloadDeployInfoDeviceTypes = () => {
            listDeviceTypes();
        };

        Bus.addListener("reloadCustomTabs", handleReloadCustomTabs);
        Bus.addListener("reloadDeployInfoDeviceTypes", handleReloadDeployInfoDeviceTypes);
        return () => {
            Bus.removeListener("reloadCustomTabs", handleReloadCustomTabs);
            Bus.removeListener("reloadDeployInfoDeviceTypes", handleReloadDeployInfoDeviceTypes);
        };
    }, [listDeviceTypes]);

    function openAddDeviceTypeModal() {
        setDeviceTypeForm({
            device_type_id: undefined,
            network_service_id: undefined,
            network_service: null,
        });
        loadAvailableDeviceTypes();
        setAvailableNetworkServices([]);
        setModalVisible(true);
    }

    async function loadAvailableDeviceTypes() {
        if (!store.currentItem.id) {
            setAvailableDeviceTypes([]);
            return;
        }

        setLoadingAvailableDeviceTypes(true);
        try {
            const [deviceTypesResponse, settingsResponse] = await Promise.all([
                api.get('/device_type?active=1'),
                api.get(`/deploy_external_services_settings/deploy/${store.currentItem.id}`),
            ]);

            const allDeviceTypes = parseResult(deviceTypesResponse.data);
            const settings = parseResult(settingsResponse.data);
            const linkedDeviceTypeIds = new Set(
                settings
                    .filter((item) => !!item.device_type_id)
                    .map((item) => Number(item.device_type_id))
            );

            const filteredDeviceTypes = allDeviceTypes.filter(
                (item) => !linkedDeviceTypeIds.has(Number(item.id))
            );
            setAvailableDeviceTypes(filteredDeviceTypes);
        } catch (error) {
            store.error = { message: translate('default.errorOccurred') };
            setAvailableDeviceTypes([]);
        } finally {
            setLoadingAvailableDeviceTypes(false);
        }
    }

    async function loadAvailableNetworkServices(selectedDeviceTypeId) {
        if (!store.currentItem.id || !selectedDeviceTypeId) {
            setAvailableNetworkServices([]);
            return;
        }

        setLoadingNetworkServices(true);
        try {
            const [networkServicesResponse, settingsResponse] = await Promise.all([
                api.get('/external_services?service_type=NS'),
                api.get(`/deploy_external_services_settings/deploy/${store.currentItem.id}`),
            ]);

            const networkServices = parseResult(networkServicesResponse.data);
            const settings = parseResult(settingsResponse.data);
            const linkedNetworkServiceIds = new Set(
                settings
                    .filter((item) =>
                        Number(item.device_type_id) === Number(selectedDeviceTypeId)
                        && !!item.network_service_id
                    )
                    .map((item) => Number(item.network_service_id))
            );

            const filteredServices = networkServices.filter(
                (item) => !linkedNetworkServiceIds.has(Number(item.id))
            );
            setAvailableNetworkServices(filteredServices);
        } catch (error) {
            store.error = { message: translate('default.errorOccurred') };
            setAvailableNetworkServices([]);
        } finally {
            setLoadingNetworkServices(false);
        }
    }

    function parseResult(data) {
        return (data && data.result) ? data.result : [];
    }

    function normalizeDefaultKeys(defaultKeys) {
        if (Array.isArray(defaultKeys)) {
            return defaultKeys.filter(item => item && item.key);
        }

        if (typeof defaultKeys === 'string') {
            try {
                const parsed = JSON.parse(defaultKeys);
                return Array.isArray(parsed) ? parsed.filter(item => item && item.key) : [];
            } catch (error) {
                return [];
            }
        }

        return [];
    }

    async function getExternalServiceById(externalServiceId) {
        const externalServiceResponse = await api.get(`/external_services/${externalServiceId}`);
        return externalServiceResponse && externalServiceResponse.data
            ? externalServiceResponse.data.result
            : null;
    }

    async function getExternalServiceTypeDefaultKeys(externalServiceId) {
        const externalServiceData = await getExternalServiceById(externalServiceId);
        const externalServiceTypeId = externalServiceData
            && (externalServiceData.external_services_type_id || externalServiceData.external_service_type_id);

        if (!externalServiceTypeId) {
            return [];
        }

        const externalServiceTypeResponse = await api.get(`/external_services_type/${externalServiceTypeId}`);
        const externalServiceTypeResult = externalServiceTypeResponse && externalServiceTypeResponse.data
            ? externalServiceTypeResponse.data.result
            : null;
        return normalizeDefaultKeys(externalServiceTypeResult && externalServiceTypeResult.default_keys);
    }

    async function createDefaultSettingsByService(deviceTypeId, serviceId, serviceField) {
        const defaultKeys = await getExternalServiceTypeDefaultKeys(serviceId);
        if (!defaultKeys.length) {
            return { created: 0, hasDefaultKeys: false };
        }

        const existingSettingsResponse = await api.get(`/deploy_external_services_settings/deploy/${store.currentItem.id}`);
        const existingSettings = parseResult(existingSettingsResponse.data);
        const existingKeySet = new Set(
            existingSettings
                .filter((item) =>
                    Number(item.device_type_id) === Number(deviceTypeId)
                    && Number(item[serviceField]) === Number(serviceId)
                )
                .map(item => item.key)
        );

        const keysToCreate = defaultKeys.filter(item => !existingKeySet.has(item.key));
        if (!keysToCreate.length) {
            return { created: 0, hasDefaultKeys: true };
        }

        const results = await Promise.allSettled(keysToCreate.map(item => api.post('/deploy_external_services_settings', {
            deploy_id: Number(store.currentItem.id),
            device_type_id: Number(deviceTypeId),
            [serviceField]: Number(serviceId),
            key: item.key,
            // API does not accept empty string in value.
            value: item.value || ' ',
            key_type: item.key_type || 'settings',
        })));

        const rejected = results.filter(result => result.status === 'rejected');
        const blockingErrors = rejected.filter((result) => {
            const status = result.reason && result.reason.response ? result.reason.response.status : undefined;
            return status !== 409;
        });

        if (blockingErrors.length) {
            throw blockingErrors[0].reason;
        }

        return { created: keysToCreate.length, hasDefaultKeys: true };
    }

    async function handleAutoCreateDefaultKeys() {
        return createDefaultSettingsByService(
            deviceTypeForm.device_type_id,
            deviceTypeForm.network_service_id,
            'network_service_id',
        );
    }

    async function handleAutoCreateDefaultKeysForAS() {
        if (!store.currentItem.application_service_id) {
            return { created: 0, hasDefaultKeys: true };
        }

        return createDefaultSettingsByService(
            deviceTypeForm.device_type_id,
            store.currentItem.application_service_id,
            'application_service_id',
        );
    }

    async function handleAddDeviceType() {
        if (!deviceTypeForm.device_type_id || !deviceTypeForm.network_service_id || !store.currentItem.id) {
            return;
        }

        setSavingDeviceType(true);
        try {
            const creationResult = await handleAutoCreateDefaultKeys();
            if (!creationResult || !creationResult.hasDefaultKeys) {
                store.error = {
                    message: `No default keys found for selected network service type (network_service_id: ${deviceTypeForm.network_service_id})`,
                };
                return;
            }

            const asCreationResult = await handleAutoCreateDefaultKeysForAS();
            if (!asCreationResult || !asCreationResult.hasDefaultKeys) {
                store.error = {
                    message: `No default keys found for selected application service type (application_service_id: ${store.currentItem.application_service_id})`,
                };
                return;
            }

            store.success = { message: 'Device type added successfully' };
            setModalVisible(false);
            listDeviceTypes();
            Bus.emit("reloadCustomTabs");
        } catch (error) {
            store.error = { message: translate('default.errorOccurred') };
        } finally {
            setSavingDeviceType(false);
        }
    }

    async function handleRemoveDeviceType(deviceTypeId) {
        if (!deviceTypeId || !store.currentItem.id) {
            return;
        }

        setRemovingDeviceTypeId(deviceTypeId);
        try {
            const response = await api.get(`/deploy_external_services_settings/deploy/${store.currentItem.id}`);
            const settings = parseResult(response.data);
            const relatedSettings = settings.filter(
                (item) => Number(item.device_type_id) === Number(deviceTypeId)
            );

            if (!relatedSettings.length) {
                store.error = { message: 'No settings found for selected device type' };
                return;
            }

            await Promise.all(relatedSettings.map((item) =>
                api.delete(`/deploy_external_services_settings/${item.id}`)
            ));

            store.success = { message: 'Device type removed successfully' };
            listDeviceTypes();
            Bus.emit("reloadCustomTabs");
        } catch (error) {
            store.error = { message: translate('default.errorOccurred') };
        } finally {
            setRemovingDeviceTypeId(undefined);
        }
    }

    function confirmRemoveDeviceType(item) {
        const deviceTypeName = item.device_type_name || item.device_type || item.device_type_id;
        Modal.confirm({
            title: `${translate('default.confirm.delete')} "${deviceTypeName}"?`,
            okText: translate('default.remove'),
            cancelText: translate('default.cancel'),
            onOk: () => handleRemoveDeviceType(item.device_type_id),
        });
    }

    const deviceTypeColumns = [
        {
            title: 'Device type',
            key: 'device_type_name',
            render: (_, item) => item.device_type_name || item.device_type || item.device_type_id,
        },
        {
            title: 'Application',
            key: 'application_service_name',
            render: (_, item) => item.application_service_name || '-',
        },
        {
            title: 'Network',
            key: 'network_service_name',
            render: (_, item) => item.network_service_name || '-',
        },
        {
            title: '',
            key: 'action',
            width: 56,
            render: (_, item) => (
                <Button
                    type="default"
                    shape="circle"
                    icon="minus"
                    title={translate('default.remove')}
                    loading={Number(removingDeviceTypeId) === Number(item.device_type_id)}
                    disabled={!!removingDeviceTypeId}
                    onClick={() => confirmRemoveDeviceType(item)}
                />
            ),
        },
    ].filter((item) => canManageDeviceTypes || item.key !== 'action');

    return useObserver(() => (
        <>
            <Form layout="vertical">
                <Row gutter={16}>
                    <Col {...labelCol}>
                        <Form.Item label="Id">
                            <Input value={store.currentItem.id} disabled={true} />
                        </Form.Item>
                    </Col>
                    <Col {...labelCol}>
                        <Form.Item label={translate('default.name')}>
                            <Input value={store.currentItem.name} onChange={handleChange.bind(this, 'name')} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col {...labelCol}>
                        <Form.Item label={translate('application.name')}>
                            <CustomAutoComplete
                                searchValue={'id'}
                                searchKey={'name'}
                                path={'/application'}
                                placeholder={translate('application.select.name')}
                                onSelect={handleComboSelect.bind(this, 'application_id')}
                                value={store.currentItem.application_id} />
                        </Form.Item>
                    </Col>
                    <Col {...labelCol}>
                        <Form.Item label={translate('deploy.applicationService')}>
                            <CustomAutoComplete
                                searchValue={'id'}
                                searchKey={'name'}
                                path={'/external_services?service_type=AS'}
                                placeholder={translate('default.select.name')}
                                onSelect={handleComboSelect.bind(this, 'application_service_id')}
                                value={store.currentItem.application_service_id} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col {...labelCol}>
                        <Form.Item label={networkServiceFallbackLabel}>
                            <CustomAutoComplete
                                searchValue={'id'}
                                searchKey={'name'}
                                path={'/external_services?service_type=NS'}
                                placeholder={translate('default.select.name')}
                                onSelect={handleComboSelect.bind(this, 'network_service_id')}
                                value={store.currentItem.network_service_id} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col {...labelCol}>
                        <Form.Item label="Device types">
                            <div style={{ border: '1px solid #e8e8e8', borderRadius: 4, padding: 8, background: '#fff', width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                    {
                                        canManageDeviceTypes && (
                                            <Button
                                                type="primary"
                                                shape="circle"
                                                icon="plus"
                                                title={translate('default.add')}
                                                disabled={!store.currentItem.id}
                                                onClick={openAddDeviceTypeModal}
                                            />
                                        )
                                    }
                                </div>
                                <Table
                                    size="small"
                                    rowKey={item => item.device_type_id}
                                    pagination={false}
                                    loading={loadingDeviceTypes}
                                    columns={deviceTypeColumns}
                                    dataSource={deviceTypes}
                                    locale={{ emptyText: translate('deploy.noDeviceTypesLinked') }}
                                />
                            </div>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <Modal
                title="Adicionar device type"
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleAddDeviceType}
                confirmLoading={savingDeviceType}
                okText={translate('default.add')}
                cancelText={translate('default.cancel')}
                okButtonProps={{ disabled: !deviceTypeForm.device_type_id || !deviceTypeForm.network_service_id }}
            >
                <Form layout="vertical">
                    <Form.Item label="Device type" required>
                        <Select
                            placeholder={translate('default.select.deviceType')}
                            value={deviceTypeForm.device_type_id}
                            loading={loadingAvailableDeviceTypes}
                            onChange={(value) => {
                                setDeviceTypeForm(prev => ({
                                    ...prev,
                                    device_type_id: value,
                                    network_service_id: undefined,
                                    network_service: null,
                                }));
                                loadAvailableNetworkServices(value);
                            }}
                            notFoundContent={translate('deploy.noAvailableDeviceType')}
                        >
                            {availableDeviceTypes.map((item) => (
                                <Option key={item.id} value={item.id}>
                                    {item.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label={translate('deploy.availableNetworkService')} required>
                        <Select
                            placeholder={translate('default.select.name')}
                            value={deviceTypeForm.network_service_id}
                            loading={loadingNetworkServices}
                            disabled={!deviceTypeForm.device_type_id}
                            onChange={(value) => {
                                const selectedObj = availableNetworkServices.find(
                                    item => String(item.id) === String(value)
                                ) || null;
                                setDeviceTypeForm(prev => ({
                                    ...prev,
                                    network_service_id: value,
                                    network_service: selectedObj,
                                }));
                            }}
                            notFoundContent={deviceTypeForm.device_type_id ? translate('deploy.noAvailableNetworkService') : translate('default.select.deviceType')}
                        >
                            {availableNetworkServices.map((item) => (
                                <Option key={item.id} value={item.id}>
                                    {item.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

        </>
    ));
}

const Deploy = () => {
    const store = React.useContext(storeContext);

    ContentForm = Form.create({})(ContentForm);

    store.columns = columns;
    store.url = '/deploy';

    store.editForm = undefined;
    ContentForm.tab = 'default.info';

    DeploySettings.tab = 'deploySettings.name'
    DeploySettings.disabled = true;

    DeployNsSettings.tab = 'deployNsSettings.name'
    DeployNsSettings.disabled = true;

    DeployAsSettings.tab = 'deployExternalServicesSettings.name'
    DeployAsSettings.disabled = true;

    store.tabs = [ContentForm, DeploySettings, DeployAsSettings, DeployNsSettings];

    return <RegistryForm />
}

export default Deploy;
